import type {
  CompletionChunk,
  CompletionRequest,
  CompletionResult,
  LLMProvider,
  PreparedRequest,
  ProviderProfile,
  TransportResponse,
} from "@roqeet/core";
import { parseSSE } from "../sse.js";
import { getExactTokenCounter } from "../../tokenizer/index.js";

/**
 * Anthropic(Claude) Provider 어댑터 — 결정 #7.
 *
 * OpenAI와 다른 점:
 *  - system 메시지는 messages 배열이 아니라 최상위 `system` 필드로 분리.
 *  - `max_tokens`가 필수.
 *  - SSE 이벤트가 타입 구분형(content_block_delta 등).
 *  - 브라우저 직접 호출 시 CORS가 막히므로 보통 proxy transport와 함께 쓴다
 *    (결정 #7). 직접 호출을 시도할 땐 dangerous-direct-browser-access 헤더 필요.
 */
export class AnthropicProvider implements LLMProvider {
  readonly kind = "anthropic" as const;

  private baseUrl(profile: ProviderProfile): string {
    return (profile.baseUrl ?? "https://api.anthropic.com/v1").replace(/\/+$/, "");
  }

  prepare(req: CompletionRequest, profile: ProviderProfile, apiKey?: string): PreparedRequest {
    const headers: Record<string, string> = {
      "content-type": "application/json",
      "anthropic-version": "2023-06-01",
      // 브라우저 direct 호출 허용 플래그(proxy 경유 시 무해).
      "anthropic-dangerous-direct-browser-access": "true",
      ...(profile.extraHeaders ?? {}),
    };
    if (apiKey) headers["x-api-key"] = apiKey;

    // system 턴을 분리하고 나머지를 user/assistant로 유지.
    const system = req.messages
      .filter((m) => m.role === "system")
      .map((m) => m.content)
      .join("\n\n");
    const messages = req.messages
      .filter((m) => m.role !== "system")
      .map((m) => ({ role: m.role, content: m.content }));

    const body: Record<string, unknown> = {
      model: req.model,
      messages,
      stream: req.stream,
      max_tokens: req.params.maxTokens ?? 1024, // Anthropic 필수
    };
    if (system) body["system"] = system;
    if (req.params.temperature != null) body["temperature"] = req.params.temperature;
    if (req.params.topP != null) body["top_p"] = req.params.topP;
    if (req.params.topK != null) body["top_k"] = req.params.topK;
    if (req.params.stop && req.params.stop.length) body["stop_sequences"] = req.params.stop;
    const thinking = thinkingConfig(req.params);
    if (thinking) body["thinking"] = thinking;
    if (req.params.extra) Object.assign(body, req.params.extra);

    const prepared: PreparedRequest = {
      url: `${this.baseUrl(profile)}/messages`,
      method: "POST",
      headers,
      body: JSON.stringify(body),
    };
    if (req.signal) prepared.signal = req.signal;
    return prepared;
  }

  async *parseStream(res: TransportResponse): AsyncIterable<CompletionChunk> {
    if (!res.stream) {
      const result = await this.parseResult(res);
      if (result.text) yield { delta: result.text };
      return;
    }
    for await (const payload of parseSSE(res.stream)) {
      let json: AnthropicStreamEvent;
      try {
        json = JSON.parse(payload) as AnthropicStreamEvent;
      } catch {
        continue;
      }
      if (json.type === "content_block_delta") {
        const d = json.delta;
        if (d?.type === "text_delta" && d.text) yield { delta: d.text };
        else if (d?.type === "thinking_delta" && d.thinking) yield { delta: "", reasoningDelta: d.thinking };
      } else if (json.type === "message_delta" && json.usage) {
        yield { delta: "", usage: mapUsage(json.usage) };
      } else if (json.type === "message_stop") {
        return;
      }
    }
  }

  async parseResult(res: TransportResponse): Promise<CompletionResult> {
    const raw = (await res.text?.()) ?? "";
    if (!res.ok) {
      throw new Error(`Anthropic 요청 실패 (HTTP ${res.status}): ${raw.slice(0, 500)}`);
    }
    const json = JSON.parse(raw) as AnthropicResult;
    const text = (json.content ?? [])
      .filter((c) => c.type === "text")
      .map((c) => c.text ?? "")
      .join("");
    const result: CompletionResult = {
      text,
      finishReason: mapStop(json.stop_reason),
    };
    if (json.model) result.model = json.model;
    if (json.usage) {
      result.usage = {
        promptTokens: json.usage.input_tokens,
        completionTokens: json.usage.output_tokens,
        totalTokens:
          json.usage.input_tokens != null || json.usage.output_tokens != null
            ? (json.usage.input_tokens ?? 0) + (json.usage.output_tokens ?? 0)
            : undefined,
      };
    }
    return result;
  }

  async countTokens(text: string, model: string): Promise<number> {
    // Claude는 공식 tiktoken 인코딩이 없어 cl100k_base를 근사로 쓴다(결정 #12).
    // char/4 휴리스틱보다 정확하다. 로드 실패 시 모듈 내부에서 휴리스틱 폴백.
    const count = await getExactTokenCounter(model);
    return count(text);
  }

  async listModels(profile: ProviderProfile, apiKey?: string): Promise<string[]> {
    const headers: Record<string, string> = {
      "anthropic-version": "2023-06-01",
      "anthropic-dangerous-direct-browser-access": "true",
      ...(profile.extraHeaders ?? {}),
    };
    if (apiKey) headers["x-api-key"] = apiKey;

    const models: string[] = [];
    let afterId: string | undefined;

    for (let page = 0; page < 20; page++) {
      const query = new URLSearchParams({ limit: "1000" });
      if (afterId) query.set("after_id", afterId);
      const res = await fetch(`${this.baseUrl(profile)}/models?${query.toString()}`, { headers });
      if (!res.ok) return unique(models);

      const json = (await res.json()) as AnthropicModelsResult;
      models.push(...(json.data ?? []).map((m) => m.id).filter(Boolean));

      if (!json.has_more || !json.last_id) break;
      afterId = json.last_id;
    }

    return unique(models);
  }
}

function unique(values: string[]): string[] {
  return [...new Set(values)];
}

function mapStop(reason?: string): CompletionResult["finishReason"] {
  switch (reason) {
    case "end_turn":
    case "stop_sequence":
      return "stop";
    case "max_tokens":
      return "length";
    default:
      return reason ? "stop" : undefined;
  }
}

function thinkingConfig(p: CompletionRequest["params"]): Record<string, unknown> | undefined {
  if (!p.reasoningLevel || p.reasoningLevel === "auto" || p.reasoningLevel === "off") return undefined;
  const maxTokens = p.maxTokens ?? 1024;
  const requested = p.reasoningBudgetTokens ?? thinkingBudgetForLevel(p.reasoningLevel);
  const budget = Math.min(requested, Math.max(0, maxTokens - 1));
  if (budget < 1024) return undefined;
  return { type: "enabled", budget_tokens: budget };
}

function thinkingBudgetForLevel(level: string): number {
  switch (level) {
    case "minimal":
      return 1024;
    case "low":
      return 2048;
    case "medium":
      return 4096;
    case "high":
      return 8192;
    case "xhigh":
      return 16384;
    default:
      return 1024;
  }
}

function mapUsage(usage: AnthropicUsage): CompletionResult["usage"] {
  return {
    promptTokens: usage.input_tokens,
    completionTokens: usage.output_tokens,
    totalTokens:
      usage.input_tokens != null || usage.output_tokens != null
        ? (usage.input_tokens ?? 0) + (usage.output_tokens ?? 0)
        : undefined,
  };
}

interface AnthropicStreamEvent {
  type: string;
  delta?: { type?: string; text?: string; thinking?: string };
  usage?: AnthropicUsage;
}

interface AnthropicResult {
  model?: string;
  content?: { type: string; text?: string }[];
  stop_reason?: string;
  usage?: AnthropicUsage;
}

interface AnthropicModelsResult {
  data?: { id: string }[];
  has_more?: boolean;
  last_id?: string;
}

interface AnthropicUsage {
  input_tokens?: number;
  output_tokens?: number;
}
